'use client'

import Link from 'next/link'
import { useState } from 'react'
import { ArrowRight, Clock, Signal } from 'lucide-react'
import { CATEGORIES, COURSES, coursePath, type CategoryId, type Course } from '@/data/catalog'
import { CourseIcon } from './CourseIcon'

type Locale = 'en' | 'ja'

const ACCENT: Record<CategoryId, { bar: string; chip: string; kanji: string }> = {
  language: { bar: 'from-brand-green to-[#2a7d64]', chip: 'bg-brand-green/10 text-brand-green', kanji: 'text-brand-green/[0.07]' },
  exam: { bar: 'from-brand-red to-[#e0476b]', chip: 'bg-brand-red/10 text-brand-red', kanji: 'text-brand-red/[0.07]' },
  career: { bar: 'from-brand-navy to-[#2c4170]', chip: 'bg-brand-navy/10 text-brand-navy', kanji: 'text-brand-navy/[0.07]' },
  professional: { bar: 'from-brand-gold to-[#ddc08f]', chip: 'bg-brand-gold/20 text-[#7a5f2c]', kanji: 'text-brand-gold/[0.16]' },
  kids: { bar: 'from-[#E8789A] to-[#f3a3b8]', chip: 'bg-[#E8789A]/15 text-[#a8385a]', kanji: 'text-[#E8789A]/[0.14]' },
}

export function CourseCard({ c, locale }: { c: Course; locale: Locale }) {
  const a = ACCENT[c.category]
  const cat = CATEGORIES.find((x) => x.id === c.category)!
  return (
    <Link
      href={coursePath(locale, c.slug)}
      className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-brand-cool-gray/80 bg-white p-6 shadow-[0_1px_2px_rgba(28,31,38,0.04)] transition-[transform,box-shadow,border-color] duration-300 ease-premium hover:-translate-y-1.5 hover:border-brand-gold/60 hover:shadow-[0_30px_60px_-30px_rgba(20,33,61,0.35)] focus-ring"
    >
      <span className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${a.bar}`} aria-hidden />
      <span
        className={`pointer-events-none absolute -right-2 -top-4 select-none font-[family-name:var(--font-noto-serif-jp)] text-[7.5rem] font-bold leading-none ${a.kanji} transition-transform duration-500 group-hover:-translate-y-1 group-hover:scale-105`}
        aria-hidden
      >
        {c.kanji}
      </span>
      <div className="relative flex items-center gap-3">
        <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${a.chip}`}>
          <CourseIcon name={c.icon} className="h-5 w-5" />
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-muted">{cat.title[locale]}</span>
      </div>
      <h3 className="relative mt-5 text-lg font-bold leading-snug text-brand-navy">{c.title[locale]}</h3>
      {locale === 'en' && <p className="relative mt-0.5 font-[family-name:var(--font-noto-sans-jp)] text-xs text-brand-red/80">{c.jpName}</p>}
      <p className="relative mt-3 flex-1 text-sm leading-relaxed text-brand-charcoal/75">{c.short[locale]}</p>
      <div className="relative mt-5 flex flex-wrap gap-2 text-xs font-medium text-brand-charcoal">
        <span className="inline-flex items-center gap-1 rounded-full bg-brand-soft-gray px-2.5 py-1">
          <Signal className="h-3.5 w-3.5 text-brand-gold" /> {c.level[locale]}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-brand-soft-gray px-2.5 py-1">
          <Clock className="h-3.5 w-3.5 text-brand-gold" /> {c.duration[locale]}
        </span>
      </div>
      <span className="relative mt-5 inline-flex items-center gap-1.5 border-t border-brand-cool-gray/70 pt-4 text-sm font-semibold text-brand-red">
        {locale === 'ja' ? 'コース詳細' : 'View course'}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </span>
    </Link>
  )
}

export function CourseExplorer({ locale, limit }: { locale: Locale; limit?: number }) {
  const [tab, setTab] = useState<CategoryId | 'all'>('all')
  const list = (tab === 'all' ? COURSES : COURSES.filter((c) => c.category === tab)).slice(0, tab === 'all' && limit ? limit : undefined)
  const tabs: { id: CategoryId | 'all'; label: string; count: number }[] = [
    { id: 'all', label: locale === 'ja' ? 'すべて' : 'All courses', count: COURSES.length },
    ...CATEGORIES.map((c) => ({ id: c.id, label: c.title[locale], count: COURSES.filter((x) => x.category === c.id).length })),
  ]
  return (
    <div>
      {/* Phones/tablets: every category visible in a grid (no hidden sideways scroll); desktop: one pill bar */}
      <div data-no-reveal>
        <div
          role="tablist"
          aria-label={locale === 'ja' ? 'コースの分類' : 'Course categories'}
          className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:mx-auto xl:flex xl:w-max xl:rounded-full xl:border xl:border-brand-cool-gray/80 xl:bg-white/80 xl:p-1.5 xl:shadow-sm xl:backdrop-blur"
        >
          {tabs.map((tb) => (
            <button
              key={tb.id}
              type="button"
              role="tab"
              aria-selected={tab === tb.id}
              onClick={() => setTab(tb.id)}
              className={`flex items-center justify-between gap-2 rounded-2xl border px-3.5 py-2.5 text-left text-sm font-semibold leading-tight transition-all xl:justify-center xl:whitespace-nowrap xl:rounded-full xl:border-0 xl:px-4 xl:py-2 ${
                tab === tb.id
                  ? 'border-brand-navy bg-brand-navy text-white shadow-md'
                  : 'border-brand-cool-gray/80 bg-white/80 text-brand-charcoal hover:bg-brand-soft-gray'
              }`}
            >
              {tb.label}
              <span className={`shrink-0 rounded-full px-1.5 text-[11px] ${tab === tb.id ? 'bg-white/20' : 'bg-brand-soft-gray text-brand-muted'}`}>{tb.count}</span>
            </button>
          ))}
        </div>
      </div>
      <div key={tab} className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {list.map((c, i) => (
          <div key={c.slug} className="ncp-pop-in" style={{ animationDelay: `${Math.min(i, 8) * 45}ms` }}>
            <CourseCard c={c} locale={locale} />
          </div>
        ))}
      </div>
    </div>
  )
}
